/**
 * Whether this deployment has voice (UC-A6).
 *
 * Fetched once and shared, so every control on the screen asks the same question once rather than
 * each one probing on mount. `mock` — the shipped default — reports disabled, and the controls are
 * then absent rather than present and failing when pressed.
 */

import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from "react";

import { fetchSpeechStatus } from "../api/client";
import type { SpeechStatus } from "../api/types";

/** Voice off, which is what a deployment that has not configured an engine gets. */
const OFF: SpeechStatus = {
  enabled: false,
  speaks: [],
  max_audio_seconds: 60,
  max_speak_characters: 1200,
  accepted_audio_types: [],
};

const SpeechContext = createContext<SpeechStatus>(OFF);

export function SpeechProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<SpeechStatus>(OFF);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const fetched = await fetchSpeechStatus();
        if (live) setStatus(fetched);
      } catch {
        // Off. A deployment whose speech endpoint is unreachable should render an interface
        // without voice, not one whose microphone fails on the first press.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  return createElement(SpeechContext.Provider, { value: status }, children);
}

export function useSpeechStatus(): SpeechStatus {
  return useContext(SpeechContext);
}
